import { useState, useEffect, useContext } from 'react'

import { StudyTelemetryContext } from './StudyTelemetryContext'

import { GLOBAL_EVENTS } from './telemetryEvents'

// Study-side twin of component/workspace/hooks/usePanelResize.
// Same PANEL_RESIZE payload ({ panel, newSize, timestamp }) on mouse-up.
//
// The study IDE has three resizable boundaries: 'left' (the description side, whose
// .chi-divider was already styled cursor: col-resize but never wired), 'terminal'
// (the console height), and 'right' (the tutor panel).
//
// 'right' keeps the workspace's panel name even though the tutor sits BELOW the doc
// here rather than in a third column — same panel, same event vocabulary, so the two
// telemetry streams stay comparable. Only the measured dimension differs: newSize is
// that panel's height here and its width in the workspace.
//
// containerRef is the whole IDE shell (.chi-root) — correct for the left edge,
// which spans the full width. bodyRef is the panel area (.chi-body), whose bottom
// edge is the one the console is measured up from; .chi-root's bottom sits below
// the footer, which would offset every dragged height by the footer's height.
// leftRef is the description column, which the tutor is measured up from.
export function useStudyPanelResize (containerRef, bodyRef, leftRef) {
  const { logTelemetryEvent } = useContext(StudyTelemetryContext)

  // Initial sizes. Percentages in chi.scss are the visual default (50% / 34%);
  // these pixel values are only adopted once the participant drags.
  const [leftWidth, setLeftWidth] = useState(null)
  const [terminalHeight, setTerminalHeight] = useState(null)
  const [chatHeight, setChatHeight] = useState(null)

  const [activeResizer, setActiveResizer] = useState(null) // 'left' | 'right' | 'terminal'

  useEffect(() => {
    if (!activeResizer) return

    const handleMouseMove = (e) => {
      if (!containerRef.current) return
      const rect = containerRef.current.getBoundingClientRect()

      if (activeResizer === 'left') {
        const newWidth = e.clientX - rect.left
        setLeftWidth(Math.max(280, Math.min(rect.width - 320, newWidth)))
      }

      if (activeResizer === 'terminal') {
        const body = bodyRef?.current
        if (!body) return
        const newHeight = body.getBoundingClientRect().bottom - e.clientY
        setTerminalHeight(Math.max(100, Math.min(600, newHeight)))
      }

      if (activeResizer === 'right') {
        const left = leftRef?.current
        if (!left) return
        const r = left.getBoundingClientRect()
        const newHeight = r.bottom - e.clientY
        // Both ends floored: the doc must stay readable and the tutor must keep its
        // composer plus a line of history, or dragging can hide one of them entirely.
        setChatHeight(Math.max(160, Math.min(r.height - 160, newHeight)))
      }
    }

    const handleMouseUp = () => {
      // Log the final panel size
      let newSize = null
      if (activeResizer === 'left') newSize = leftWidth
      if (activeResizer === 'terminal') newSize = terminalHeight
      if (activeResizer === 'right') newSize = chatHeight

      logTelemetryEvent(GLOBAL_EVENTS.PANEL_RESIZE, {
        panel: activeResizer,
        newSize: newSize,
        timestamp: Date.now()
      })

      setActiveResizer(null)
      document.body.style.cursor = 'default'
      document.body.style.userSelect = 'auto'
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    document.body.style.userSelect = 'none'
    document.body.style.cursor = activeResizer === 'left' ? 'col-resize' : 'row-resize'
    // 'right' is a horizontal boundary here, so it takes row-resize like 'terminal'
    // — the workspace's vertical 'right' edge is the one place these two diverge.

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [activeResizer, containerRef, bodyRef, leftRef, leftWidth, terminalHeight, chatHeight, logTelemetryEvent])

  return {
    leftWidth,
    terminalHeight,
    chatHeight,
    startDraggingLeft: (e) => { e.preventDefault(); setActiveResizer('left') },
    startDraggingTerminal: (e) => { e.preventDefault(); setActiveResizer('terminal') },
    startDraggingChat: (e) => { e.preventDefault(); setActiveResizer('right') }
  }
}
